import { Check, Heart, ShieldCheck } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import {
  computeFundTotals,
  encouragementMessage,
  formatMonths,
  formatPeaceStatus,
  formatTargetDate,
  parseAmount,
} from '../../lib/emergencyFundDisplayUtils';
import { formatEUR } from '../../utils/money';
import { Badge } from '../ui/Badge';
import { EmergencyFundPeaceRing } from './EmergencyFundPeaceRing';

function hasEnoughSelfies(health) {
  return Number(health?.snapshots_used ?? 0) >= 3;
}

function PathRow({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between gap-space-3">
      <span className="type-label font-normal text-muted-foreground">{label}</span>
      <span
        className={
          tone === 'sage'
            ? 'type-body-sm shrink-0 text-right font-bold text-sage-on-tint'
            : 'type-body-sm shrink-0 text-right font-bold text-foreground'
        }
      >
        {value}
      </span>
    </div>
  );
}

export function EmergencyFundPeaceCard({
  health,
  storedTotal,
  targetEur,
  targetMonths,
  monthsCovered,
  healthMonthsCovered,
  suggestedMonthly,
  monthsHorizon,
  yearProgress,
}) {
  const copy = pt.emergencyFund.dashboard.peace;
  const pathCopy = pt.emergencyFund.dashboard.howToReach;
  const { saved, target } = health ? computeFundTotals(health) : { saved: 0, target: 0 };
  const displaySaved = saved > 0 ? saved : storedTotal;
  const displayTarget = target > 0 ? target : parseAmount(targetEur);
  const displayGap = Math.max(0, displayTarget - displaySaved);
  const hasHealthData = health && hasEnoughSelfies(health);
  const statusLine = formatPeaceStatus(monthsCovered, copy);

  const monthly = Number(suggestedMonthly ?? 0);
  const hasMonthly = monthly > 0;
  const hasYearProgress = yearProgress != null && yearProgress !== 0;
  const encouragement = encouragementMessage(monthsCovered, pathCopy.encouragement);

  return (
    <section className="flex w-full flex-col items-center gap-space-4 rounded-ds-xl border border-border bg-card p-space-5 shadow-sm dark:shadow-none">
      <EmergencyFundPeaceRing
        monthsCovered={monthsCovered}
        targetMonths={targetMonths}
        className="mx-auto"
      />

      <div className="flex w-full flex-col items-center gap-1 text-center">
        <p className="type-label font-normal text-muted-foreground">
          {copy.monthsTarget.replace('{n}', String(targetMonths))}
        </p>
        <h2 className="type-display-stat text-foreground">{statusLine}</h2>
        <p
          className="type-body-sm text-muted-foreground [&_b]:font-bold [&_b]:text-foreground"
          dangerouslySetInnerHTML={{
            __html: copy.moneyTemplate
              .replace('{saved}', formatEUR(displaySaved))
              .replace('{target}', formatEUR(displayTarget))
              .replace('{months}', String(targetMonths)),
          }}
        />

        {hasHealthData && Number(health.avg_monthly_expenses) > 0 ? (
          <p className="type-label font-normal text-muted-foreground">
            {healthMonthsCovered != null
              ? copy.healthCoverage
                  .replace('{amount}', formatEUR(Number(health.avg_monthly_expenses)))
                  .replace('{n}', formatMonths(healthMonthsCovered))
              : `Despesa total média: ${formatEUR(Number(health.avg_monthly_expenses))}/mês`}
          </p>
        ) : null}

        {!hasHealthData && health ? (
          <p className="type-label font-normal text-muted-foreground">
            {copy.awaitingSelfies.replace(
              '{count}',
              String(Math.max(1, 3 - Number(health.snapshots_used ?? 0))),
            )}
          </p>
        ) : null}
      </div>

      <Badge status={displayGap > 0 ? 'sage' : 'success'} className="px-space-3 py-space-1">
        {displayGap > 0 ? (
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : (
          <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        {displayGap > 0 ? copy.goalGapTemplate.replace('{gap}', formatEUR(displayGap)) : copy.goalComplete}
      </Badge>

      <div className="h-px w-full bg-border" />

      <div className="flex w-full flex-col gap-space-2-5">
        <p className="type-section text-muted-foreground">{pathCopy.title}</p>

        {hasMonthly ? (
          <PathRow label={pathCopy.savingLabel} value={pathCopy.savingValue.replace('{monthly}', formatEUR(monthly))} />
        ) : null}
        {hasMonthly ? (
          <PathRow label={pathCopy.reachLabel} value={formatTargetDate(monthsHorizon)} />
        ) : null}
        {hasYearProgress ? (
          <PathRow
            label={pathCopy.progressRow}
            value={`${yearProgress > 0 ? '+' : ''}${formatEUR(yearProgress)}`}
            tone="sage"
          />
        ) : null}

        <div className="flex gap-space-2 rounded-ds-md border border-sage-border bg-sage-tint px-space-3 py-space-2-5 text-sage-on-tint">
          <Heart className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span className="type-body-sm leading-relaxed">{encouragement}</span>
        </div>
      </div>
    </section>
  );
}

export default EmergencyFundPeaceCard;
